// Route table for the portal, one entry per folder under src/app (the layout
// Next.js used before the move: `(group)` folders add no path, `[id]` is
// `:id`, `_private` folders hold components, `layout.tsx` wraps its folder
// and `page.tsx` is the folder's index). Add a route here when you add a page.
import { Outlet, type RouteObject } from "react-router";

import { PORTALS_ENABLED } from "@rai/core/lib/portals";
import { NotFound } from "./not-found";
import { RedirectPrefix } from "./redirect-prefix";

type Module = { default: React.ComponentType };
type LayoutModule = { default: React.ComponentType<{ children: React.ReactNode }> };

const page = (load: () => Promise<Module>): Pick<RouteObject, "lazy"> => ({
  lazy: async () => ({ Component: (await load()).default }),
});

const layout = (load: () => Promise<LayoutModule>): Pick<RouteObject, "lazy"> => ({
  lazy: async () => {
    const { default: Layout } = await load();
    return { Component: () => <Layout><Outlet /></Layout> };
  },
});

/** Shared quotation, bill and request-for-quotation links always work, even while the portals are hidden. */
const shared: RouteObject[] = [
  {
    path: "b",
    children: [
        {
          path: ":token",
          handle: { title: "Your bill" },
          children: [
            { index: true, ...page(() => import("./app/b/[token]/page")) },
          ],
        },
    ],
  },
  {
    path: "q",
    children: [
        {
          path: ":token",
          handle: { title: "Shared quotation" },
          children: [
            { index: true, ...page(() => import("./app/q/[token]/page")) },
          ],
        },
    ],
  },
  {
    path: "rfq",
    children: [
        {
          path: ":token",
          handle: { title: "Request for quotation" },
          children: [
            { index: true, ...page(() => import("./app/rfq/[token]/page")) },
            {
              path: "edit",
              children: [
                { index: true, ...page(() => import("./app/rfq/[token]/page")) },
              ],
            },
          ],
        },
    ],
  },
];

/** Customer account, operations console and partner areas (hidden in production until launch). */
const portals: RouteObject[] = [
  // app.raicomputer.com itself: the workspace's product landing page.
  { index: true, handle: { title: "Free IT workspace" }, ...page(() => import("./app/page")) },
  // Devices became assets (physical or digital); keep old links and bookmarks working.
  { path: "account/devices/*", element: <RedirectPrefix from="/account/devices" to="/account/assets" /> },
  { path: "admin/devices/*", element: <RedirectPrefix from="/admin/devices" to="/admin/assets" /> },
  { path: "admin/customers/:uid/devices/*", element: <RedirectPrefix from="/devices" to="/assets" /> },
  {
    path: "account",
    children: [
      {
        ...layout(() => import("./app/account/(portal)/layout")),
        handle: { title: "Your account" },
        children: [
          { index: true, ...page(() => import("./app/account/(portal)/page")) },
          {
            path: "assets",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/assets/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/assets/[id]/page")) },
                  {
                    path: "edit",
                    children: [
                      { index: true, ...page(() => import("./app/account/(portal)/assets/[id]/edit/page")) },
                    ],
                  },
                ],
              },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/assets/new/page")) },
                ],
              },
            ],
          },
          {
            path: "bills",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/bills/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/bills/[id]/page")) },
                ],
              },
            ],
          },
          {
            path: "calls",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/calls/page")) },
            ],
          },
          {
            path: "helpdesk",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/helpdesk/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/helpdesk/[id]/page")) },
                ],
              },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/helpdesk/new/page")) },
                ],
              },
              {
                path: "reports",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/helpdesk/reports/page")) },
                ],
              },
              {
                path: "settings",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/helpdesk/settings/page")) },
                ],
              },
            ],
          },
          {
            path: "invite",
            children: [
              {
                path: ":token",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/invite/[token]/page")) },
                ],
              },
            ],
          },
          {
            path: "locations",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/locations/page")) },
            ],
          },
          {
            path: "notifications",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/notifications/page")) },
            ],
          },
          {
            path: "orders",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/orders/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/orders/[id]/page")) },
                ],
              },
            ],
          },
          {
            path: "profile",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/profile/page")) },
            ],
          },
          {
            path: "quotations",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/quotations/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/quotations/[id]/page")) },
                ],
              },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/quotations/new/page")) },
                ],
              },
            ],
          },
          {
            path: "quotes",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/quotes/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/quotes/[id]/page")) },
                ],
              },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/quotes/new/page")) },
                ],
              },
            ],
          },
          {
            path: "services",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/services/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/services/[id]/page")) },
                ],
              },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/services/new/page")) },
                ],
              },
            ],
          },
          {
            path: "support",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/support/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/support/[id]/page")) },
                ],
              },
            ],
          },
          {
            path: "team",
            children: [
              { index: true, ...page(() => import("./app/account/(portal)/team/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/account/(portal)/team/[id]/page")) },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "login",
        handle: { title: "Sign in to your account" },
        children: [
          { index: true, ...page(() => import("./app/account/login/page")) },
        ],
      },
      {
        path: "act-as/:id",
        handle: { title: "Open as Rai Support" },
        children: [
          { index: true, ...page(() => import("./app/account/act-as/[id]/page")) },
        ],
      },
      {
        path: "setup",
        handle: { title: "Set up your account" },
        children: [
          { index: true, ...page(() => import("./app/account/setup/page")) },
        ],
      },
      {
        path: "welcome",
        handle: { title: "Welcome to Rai Computer" },
        children: [
          { index: true, ...page(() => import("./app/account/welcome/page")) },
        ],
      },
    ],
  },
  {
    path: "admin",
    handle: { title: "Operations console" },
    children: [
      {
        ...layout(() => import("./app/admin/(console)/layout")),
        children: [
          { index: true, ...page(() => import("./app/admin/(console)/page")) },
          {
            path: "accounts",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/accounts/page")) },
            ],
          },
          {
            path: "actions",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/actions/page")) },
            ],
          },
          {
            path: "assets",
            children: [
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/assets/[id]/page")) },
                ],
              },
            ],
          },
          {
            path: "bills",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/bills/page")) },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/bills/new/page")) },
                ],
              },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/bills/[id]/page")) },
                ],
              },
            ],
          },
          {
            path: "settings",
            children: [
              {
                path: "finance",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/settings/finance/page")) },
                ],
              },
            ],
          },
          {
            path: "managed",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/managed/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/managed/[id]/page")) },
                ],
              },
            ],
          },
          {
            path: "businesses",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/businesses/page")) },
              {
                path: ":uid",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/businesses/[uid]/page")) },
                ],
              },
            ],
          },
          {
            path: "catalog",
            ...layout(() => import("./app/admin/(console)/catalog/layout")),
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/catalog/page")) },
              {
                path: "brands",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/catalog/brands/page")) },
                ],
              },
              {
                path: "categories",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/catalog/categories/page")) },
                  {
                    path: ":id",
                    children: [
                      { index: true, ...page(() => import("./app/admin/(console)/catalog/categories/[id]/page")) },
                    ],
                  },
                ],
              },
              {
                path: "history",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/catalog/history/page")) },
                ],
              },
              {
                path: "import",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/catalog/import/page")) },
                ],
              },
              {
                path: "products",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/catalog/products/page")) },
                  {
                    path: ":id",
                    children: [
                      { index: true, ...page(() => import("./app/admin/(console)/catalog/products/[id]/page")) },
                    ],
                  },
                  {
                    path: "new",
                    children: [
                      { index: true, ...page(() => import("./app/admin/(console)/catalog/products/new/page")) },
                    ],
                  },
                ],
              },
              {
                path: "reference",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/catalog/reference/page")) },
                ],
              },
              {
                path: "services",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/catalog/services/page")) },
                  {
                    path: ":id",
                    children: [
                      { index: true, ...page(() => import("./app/admin/(console)/catalog/services/[id]/page")) },
                    ],
                  },
                  {
                    path: "new",
                    children: [
                      { index: true, ...page(() => import("./app/admin/(console)/catalog/services/new/page")) },
                    ],
                  },
                ],
              },
              {
                path: "specs",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/catalog/specs/page")) },
                  {
                    path: ":id",
                    children: [
                      { index: true, ...page(() => import("./app/admin/(console)/catalog/specs/[id]/page")) },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "customers",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/customers/page")) },
              {
                path: ":uid",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/customers/[uid]/page")) },
                  {
                    path: "assets",
                    children: [
                      {
                        path: ":id",
                        children: [
                          { index: true, ...page(() => import("./app/admin/(console)/customers/[uid]/assets/[id]/page")) },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "jobs",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/jobs/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/jobs/[id]/page")) },
                ],
              },
            ],
          },
          {
            path: "leads",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/leads/page")) },
            ],
          },
          {
            path: "marketplace",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/marketplace/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/marketplace/[id]/page")) },
                ],
              },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/marketplace/new/page")) },
                ],
              },
            ],
          },
          {
            path: "notices",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/notices/page")) },
            ],
          },
          {
            path: "orders",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/orders/page")) },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/orders/new/page")) },
                ],
              },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/orders/[id]/page")) },
                ],
              },
            ],
          },
          {
            path: "partners",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/partners/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/partners/[id]/page")) },
                ],
              },
              {
                path: "activity",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/partners/activity/page")) },
                ],
              },
            ],
          },
          {
            path: "procurement",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/procurement/page")) },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/procurement/new/page")) },
                ],
              },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/procurement/[id]/page")) },
                ],
              },
            ],
          },
          {
            path: "quotes",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/quotes/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/quotes/[id]/page")) },
                ],
              },
              {
                path: "new",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/quotes/new/page")) },
                ],
              },
            ],
          },
          {
            path: "support",
            children: [
              { index: true, ...page(() => import("./app/admin/(console)/support/page")) },
              {
                path: ":id",
                children: [
                  { index: true, ...page(() => import("./app/admin/(console)/support/[id]/page")) },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "login",
        children: [
          { index: true, ...page(() => import("./app/admin/login/page")) },
        ],
      },
    ],
  },
  {
    path: "partners",
    children: [
      {
        path: "dashboard",
        ...layout(() => import("./app/partners/dashboard/layout")),
        handle: { title: "Dashboard" },
        children: [
          { index: true, ...page(() => import("./app/partners/dashboard/page")) },
          {
            path: "billing",
            children: [
              { index: true, ...page(() => import("./app/partners/dashboard/billing/page")) },
            ],
          },
          {
            path: "expenses",
            children: [
              { index: true, ...page(() => import("./app/partners/dashboard/expenses/page")) },
            ],
          },
          {
            path: "inventory",
            children: [
              { index: true, ...page(() => import("./app/partners/dashboard/inventory/page")) },
            ],
          },
          {
            path: "profile",
            children: [
              { index: true, ...page(() => import("./app/partners/dashboard/profile/page")) },
            ],
          },
          {
            path: "quotations",
            children: [
              { index: true, ...page(() => import("./app/partners/dashboard/quotations/page")) },
            ],
          },
          {
            path: "reports",
            children: [
              { index: true, ...page(() => import("./app/partners/dashboard/reports/page")) },
            ],
          },
          {
            path: "service-areas",
            children: [
              { index: true, ...page(() => import("./app/partners/dashboard/service-areas/page")) },
            ],
          },
          {
            path: "store",
            children: [
              { index: true, ...page(() => import("./app/partners/dashboard/store/page")) },
            ],
          },
        ],
      },
      {
        path: "login",
        handle: { title: "Partner login and registration" },
        children: [
          { index: true, ...page(() => import("./app/partners/login/page")) },
        ],
      },
      {
        path: "onboarding",
        handle: { title: "Set up your business" },
        children: [
          { index: true, ...page(() => import("./app/partners/onboarding/page")) },
        ],
      },
      {
        path: "workspace",
        handle: { title: "Business workspace" },
        children: [
          {
            ...layout(() => import("./app/partners/workspace/(app)/layout")),
            children: [
              { index: true, ...page(() => import("./app/partners/workspace/(app)/page")) },
              {
                path: "actions",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/actions/page")) },
                ],
              },
              {
                path: "branding",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/branding/page")) },
                ],
              },
              {
                path: "customers",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/customers/page")) },
                  {
                    path: ":id",
                    children: [
                      { index: true, ...page(() => import("./app/partners/workspace/(app)/customers/[id]/page")) },
                    ],
                  },
                ],
              },
              {
                path: "inventory",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/inventory/page")) },
                  {
                    path: ":id",
                    children: [
                      { index: true, ...page(() => import("./app/partners/workspace/(app)/inventory/[id]/page")) },
                    ],
                  },
                ],
              },
              {
                path: "invoices",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/invoices/page")) },
                  {
                    path: ":id",
                    children: [
                      { index: true, ...page(() => import("./app/partners/workspace/(app)/invoices/[id]/page")) },
                    ],
                  },
                  {
                    path: "new",
                    children: [
                      { index: true, ...page(() => import("./app/partners/workspace/(app)/invoices/new/page")) },
                    ],
                  },
                ],
              },
              {
                path: "orders",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/orders/page")) },
                  {
                    path: ":id",
                    children: [
                      { index: true, ...page(() => import("./app/partners/workspace/(app)/orders/[id]/page")) },
                    ],
                  },
                ],
              },
              {
                path: "payments",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/payments/page")) },
                ],
              },
              {
                path: "quotations",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/quotations/page")) },
                  {
                    path: ":id",
                    children: [
                      { index: true, ...page(() => import("./app/partners/workspace/(app)/quotations/[id]/page")) },
                    ],
                  },
                ],
              },
              {
                path: "settings",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/settings/page")) },
                ],
              },
              {
                path: "team",
                children: [
                  { index: true, ...page(() => import("./app/partners/workspace/(app)/team/page")) },
                ],
              },
            ],
          },
          {
            path: "login",
            children: [
              { index: true, ...page(() => import("./app/partners/workspace/login/page")) },
            ],
          },
          {
            path: "register",
            children: [
              { index: true, ...page(() => import("./app/partners/workspace/register/page")) },
            ],
          },
        ],
      },
    ],
  },
];

export const routes: RouteObject[] = [...shared, ...(PORTALS_ENABLED ? portals : []), { path: "*", Component: NotFound }];
